// src/components/TaskCard.tsx
import { useEffect, useState } from "react";
import type {
  ID,
  Task,
  TaskStatus,
  Priority,
  Label,
  Subtask,
} from "../../types/domain";
import { updateTask, deleteTask } from "../../services/tasks";
import {
  listSubtasks,
  createSubtask,
  updateSubtask,
  deleteSubtask,
  reorderSubtasks,
} from "../../services/subtasks";
import { replaceTaskLabels } from "../../services/labels";
import { confirm } from "../../lib/dialogs";
import { toast } from "react-hot-toast";
import { FiEye, FiEdit3, FiArchive, FiRotateCcw, FiTrash2 } from "react-icons/fi";
import type {
  DraggableProvided,
  DraggableStateSnapshot,
} from "@hello-pangea/dnd";

export default function TaskCard({
  task,
  labels,
  subStat,
  allLabels,
  openDetail,
  reload,
  provided,
  snapshot,
}: {
  task: Task;
  labels: Label[];
  subStat: { total: number; done: number };
  allLabels: Label[];
  openDetail: () => void;
  reload: () => Promise<void>;
  provided?: DraggableProvided;
  snapshot?: DraggableStateSnapshot;
}) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(task.title);
  const [priority, setPriority] = useState<Priority | "">(task.priority ?? "");
  const [deadline, setDeadline] = useState(toInputDate(task.deadline || ""));
  const [desc, setDesc] = useState(task.description ?? "");
  const [status, setStatus] = useState<TaskStatus>(task.status);
  const [selectedLabels, setSelectedLabels] = useState<ID[]>(labels.map((l) => l.id));

  const [subs, setSubs] = useState<Subtask[] | null>(null);
  const [newSubTitle, setNewSubTitle] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setTitle(task.title);
    setPriority(task.priority ?? "");
    setDeadline(toInputDate(task.deadline || ""));
    setDesc(task.description ?? "");
    setStatus(task.status);
  }, [task]);
  useEffect(() => {
    setSelectedLabels(labels.map((l) => l.id));
  }, [labels]);

  const openEdit = async () => {
    setEditing(true);
    const { data } = await listSubtasks(task.id);
    setSubs(
      (data ?? []).sort(
        (a, b) => (a.position ?? 0) - (b.position ?? 0) || a.id.localeCompare(b.id)
      )
    );
  };

  const saveEdit = async () => {
    setSaving(true);
    const patch = {
      title: title.trim() || task.title,
      description: desc.trim() || null,
      priority: (priority || null) as Priority | null,
      deadline: deadline || null,
      status,
    };
    const up1 = await updateTask(task.id, patch);
    const up2 = await replaceTaskLabels(task.id, selectedLabels);
    setSaving(false);
    if (up1.error || up2.error) {
      toast.error("No se pudo guardar");
      return;
    }
    toast.success("Guardado");
    setEditing(false);
    await reload();
  };

  const archiveRestore = async () => {
    const willArchive = task.status !== "archived" && task.status !== "deleted";
    const ok = await confirm({
      title: willArchive ? "Archivar tarea" : "Restaurar tarea",
      text: willArchive ? "Se moverá a Archivadas." : "Volverá al tablero.",
      icon: "warning",
      confirmText: willArchive ? "Archivar" : "Restaurar",
    });
    if (!ok) return;
    const { error } = await updateTask(task.id, {
      status: willArchive ? "archived" : "todo",
    });
    if (error) {
      toast.error("No se pudo actualizar");
      return;
    }
    toast.success(willArchive ? "Archivada" : "Restaurada");
    await reload();
  };

  const remove = async () => {
    const ok = await confirm({
      title: "Eliminar tarea",
      text: "No se puede deshacer.",
      icon: "warning",
      confirmText: "Eliminar",
    });
    if (!ok) return;
    const { error } = await deleteTask(task.id);
    if (error) {
      toast.error("No se pudo eliminar");
      return;
    }
    toast.success("Eliminada");
    await reload();
  };

  // Subtareas
  const addSub = async () => {
    const t = newSubTitle.trim();
    if (!t) return;
    const { error } = await createSubtask({ task_id: task.id, title: t });
    if (error) return toast.error("No se pudo crear la subtarea");
    setNewSubTitle("");
    const { data } = await listSubtasks(task.id);
    setSubs(data ?? []);
  };
  const toggleSub = async (s: Subtask) => {
    const { error } = await updateSubtask(s.id, { done: !s.done });
    if (error) return toast.error("No se pudo actualizar");
    setSubs((prev) =>
      prev ? prev.map((x) => (x.id === s.id ? { ...x, done: !x.done } : x)) : prev
    );
  };
  const renameSub = async (s: Subtask, t: string) => {
    const { error } = await updateSubtask(s.id, { title: t.trim() || s.title });
    if (error) return toast.error("No se pudo renombrar");
    setSubs((prev) =>
      prev ? prev.map((x) => (x.id === s.id ? { ...x, title: t.trim() || s.title } : x)) : prev
    );
  };
  const removeSub = async (s: Subtask) => {
    const ok = await confirm({
      title: "Eliminar subtarea",
      text: "No se puede deshacer.",
      icon: "warning",
      confirmText: "Eliminar",
    });
    if (!ok) return;
    const { error } = await deleteSubtask(s.id);
    if (error) return toast.error("No se pudo eliminar");
    setSubs((prev) => (prev ? prev.filter((x) => x.id !== s.id) : prev));
  };
  const moveSub = async (index: number, dir: -1 | 1) => {
    if (!subs) return;
    const next = index + dir;
    if (next < 0 || next >= subs.length) return;
    const arr = subs.slice();
    const [item] = arr.splice(index, 1);
    arr.splice(next, 0, item);
    await reorderSubtasks(task.id, arr.map((x) => x.id));
    setSubs(arr);
  };

  const pct = subStat.total > 0 ? Math.round((subStat.done / subStat.total) * 100) : 0;
  const prioBg =
    task.priority === "low"
      ? "prio-bg-low"
      : task.priority === "medium"
      ? "prio-bg-medium"
      : task.priority === "high"
      ? "prio-bg-high"
      : task.priority === "urgent"
      ? "prio-bg-urgent"
      : "";
  
  // Calculate status bar color based on task status and deadline
  const getStatusBarClass = () => {
    // Status-based colors (highest priority)
    if (task.status === "archived") return "status-archived"; // Blue
    if (task.status === "done") return "status-done"; // White/light
    
    // Deadline-based colors for active tasks
    if (task.deadline) {
      try {
        const d = new Date(task.deadline);
        if (!Number.isNaN(d.getTime())) {
          const today = new Date();
          d.setHours(0, 0, 0, 0);
          today.setHours(0, 0, 0, 0);
          const diffDays = Math.round((d.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
          
          if (diffDays < 0) return "deadline-expired"; // Purple (expired)
          if (diffDays <= 3) return "deadline-urgent"; // Red (3 days or less)
          if (diffDays <= 7) return "deadline-warning"; // Yellow (7 days or less)
          return "deadline-safe"; // Green (more than 7 days)
        }
      } catch {
        return "deadline-safe";
      }
    }
    
    // Default for tasks without deadline
    return "deadline-safe";
  };

  const statusBarClass = getStatusBarClass();

  return (
    <article
      ref={provided?.innerRef}
      {...provided?.draggableProps}
      {...provided?.dragHandleProps}
      className={`kb-card ${editing ? "is-editing" : ""} ${prioBg} status-${task.status} ${statusBarClass}`}
      style={{
        ...(provided?.draggableProps.style ?? {}),
        opacity: snapshot?.isDragging ? 0.95 : 1,
      }}
      data-dragging={snapshot?.isDragging ? "true" : undefined}
      onDoubleClick={() => openDetail()}
      aria-label={`Tarea: ${task.title}`}
    >
      {!editing ? (
        <>
          <header className="kb-card-top">
            <h3 className="kb-card-title">{task.title}</h3>
            {task.priority && (
              <span className={`prio prio-${task.priority}`}>{PRIORITY_LABEL[task.priority]}</span>
            )}
          </header>
          {task.description && <p className="kb-card-desc">{task.description}</p>}

          <footer className="kb-card-footer">
            <div className="kb-card-meta">
              {task.deadline && <span className="due">📅 {shortDate(task.deadline)}</span>}
            </div>
            <div className="kb-card-actions">
              <button className="kb-card-action-btn" onClick={() => openDetail()} title="Ver detalle">
                <FiEye size={14} />
              </button>
              <button className="kb-card-action-btn" onClick={openEdit} title="Editar">
                <FiEdit3 size={14} />
              </button>
              <button
                className="kb-card-action-btn"
                onClick={archiveRestore}
                title={task.status === "archived" ? "Restaurar" : "Archivar"}
              >
                {task.status === "archived" ? <FiRotateCcw size={14} /> : <FiArchive size={14} />}
              </button>
              <button className="kb-card-action-btn danger" onClick={remove} title="Eliminar">
                <FiTrash2 size={14} />
              </button>
            </div>
          </footer>

          {labels.length > 0 && (
            <div className="kb-card-labels">
              {labels.map((l) => (
                <span
                  key={l.id}
                  className="lbl"
                  style={{ background: l.color || "rgba(255,255,255,.15)" }}
                >
                  {l.name}
                </span>
              ))}
            </div>
          )}

          {subStat.total > 0 && (
            <div className="kb-card-substats">
              <div className="kb-card-subbar">
                <div className="fill" style={{ width: `${pct}%` }} />
              </div>
              <div className="kb-card-subtext">
                {subStat.done}/{subStat.total} subtareas
              </div>
            </div>
          )}
        </>
      ) : (
        <form
          className="kb-card-edit"
          onSubmit={(e) => {
            e.preventDefault();
            void saveEdit();
          }}
        >
          <input
            className="kb-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Título"
          />
          <textarea
            className="kb-textarea"
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            placeholder="Descripción"
          />
          <div className="kb-edit-row">
            <select
              className="kb-select"
              value={priority}
              onChange={(e) => setPriority((e.target.value as Priority | "") || "")}
            >
              <option value="">Sin prioridad</option>
              <option value="low">Baja</option>
              <option value="medium">Media</option>
              <option value="high">Alta</option>
              <option value="urgent">Urgente</option>
            </select>
            <input
              type="date"
              className="kb-input"
              value={deadline}
              onChange={(e) => setDeadline(e.target.value)}
            />
          </div>

          <div className="kb-edit-row">
            <select
              className="kb-select"
              value={status}
              onChange={(e) => setStatus(e.target.value as TaskStatus)}
            >
              {ALL_STATUSES.filter((s) => s !== "deleted").map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABEL[s]}
                </option>
              ))}
            </select>
            <div />
          </div>

          <div className="kb-card-labels">
            {allLabels.map((l) => (
              <label key={l.id} className="lbl">
                <input
                  type="checkbox"
                  checked={selectedLabels.includes(l.id)}
                  onChange={() =>
                    setSelectedLabels((prev) =>
                      prev.includes(l.id)
                        ? prev.filter((x) => x !== l.id)
                        : [...prev, l.id]
                    )
                  }
                />
                <span className="lbl-name">{l.name}</span>
              </label>
            ))}
          </div>

          {subs && (
            <div className="kb-subs">
              <h4>Subtareas</h4>
              <ul>
                {subs.map((s, i) => (
                  <li key={s.id} className={s.done ? "done" : ""}>
                    <input
                      type="checkbox"
                      checked={s.done}
                      onChange={() => void toggleSub(s)}
                    />
                    <input
                      className="kb-input"
                      value={s.title}
                      onChange={(e) => void renameSub(s, e.target.value)}
                    />
                    <div className="kb-sub-actions">
                      <button type="button" onClick={() => void moveSub(i, -1)}>
                        ↑
                      </button>
                      <button type="button" onClick={() => void moveSub(i, 1)}>
                        ↓
                      </button>
                      <button type="button" onClick={() => void removeSub(s)}>
                        🗑️
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  void addSub();
                }}
              >
                <input
                  className="kb-input"
                  value={newSubTitle}
                  onChange={(e) => setNewSubTitle(e.target.value)}
                  placeholder="Nueva subtarea"
                />
                <button className="btn" type="submit">
                  Añadir
                </button>
              </form>
            </div>
          )}

          <div className="kb-edit-actions">
            <button className="btn btn-primary" type="submit" disabled={saving}>
              {saving ? "Guardando…" : "Guardar"}
            </button>
            <button
              className="btn btn-ghost"
              type="button"
              onClick={() => setEditing(false)}
              disabled={saving}
            >
              Cancelar
            </button>
          </div>
        </form>
      )}
    </article>
  );
}

const ALL_STATUSES: TaskStatus[] = [
  "backlog",
  "todo",
  "in_progress",
  "in_review",
  "done",
  "archived",
  "deleted",
];
const STATUS_LABEL: Record<TaskStatus, string> = {
  backlog: "Backlog",
  todo: "To do",
  in_progress: "In progress",
  in_review: "In review",
  done: "Done",
  archived: "Archivadas/Eliminadas",
  deleted: "Eliminada",
};

const PRIORITY_LABEL: Record<Priority, string> = {
  low: "Baja",
  medium: "Media",
  high: "Alta",
  urgent: "Urgente",
};

function toInputDate(value: string) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value.length === 10 ? value : "";
  const y = d.getFullYear();
  const m = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function shortDate(d: string) {
  try {
    const dt = new Date(d);
    return !Number.isNaN(dt.getTime()) ? dt.toLocaleDateString() : d;
  } catch {
    return d;
  }
}


